import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import LoadingSpinner from "../../components/LoadingSpinner";
import axiosInstance from "../../api/axiosInstance";
import { useSearchParams } from "react-router-dom";
import PaginationButtons from "../../components/PaginationButtons";
import OwnerCard from "./OwnerCard";
import RangeInputs from "../../components/RangeInputs";
import { propertyTypeLabels } from "../../utils/common";

const TransferHistoryOwnerView = () => {
  const [formData, setFormData] = useState({
    city: "",
    zip: "",
    state: "",
    county: "",
    period: "last_6_months",
    propertyType: "",
    stillOwner: false,
    bought: [null, null],
    sold: [null, null],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [owners, setOwners] = useState([]);
  const [openCard, setOpenCard] = useState(null);

  const leadsPerPage = 20;
  const [totalOwners, setTotalOwners] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);

  // Update URL when formData changes
  useEffect(() => {
    const newParams = new URLSearchParams();

    Object.entries(formData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Serialize bought/sold arrays as comma-separated values
        if (value.some((v) => v !== null)) newParams.set(key, value.join(","));
      } else if (value !== "" && value !== null && value !== undefined) {
        newParams.set(key, value);
      }
    });

    newParams.set("page", 1);
    setSearchParams(newParams);
    setCurrentPage(1);
    // eslint-disable-next-line
  }, [formData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Parse params when component mounts
  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());

    const parseRange = (param) => {
      const value = params[param];
      if (!value || value === "null" || value === "") return [null, null];
      const parts = value.split(",").map((v) => (v === "null" ? null : Number(v) || null));
      return parts.length === 2 ? parts : [parts[0] ?? null, null];
    };

    setFormData((prev) => ({
      ...prev,
      ...params,
      stillOwner: params.stillOwner === "true" ? true : params.stillOwner === "false" ? false : false,
      bought: parseRange("bought"),
      sold: parseRange("sold"),
    }));

    setCurrentPage(Number(params.page) || 1);
    // eslint-disable-next-line
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { zip, city, state } = formData;

    if ((!zip || !zip.trim()) && (!city || !city.trim() || !state || !state.trim())) {
      toast.error("Either ZIP or both City and State are required to search.");
      return;
    }

    await fetchOwners(1);
  };

  const fetchOwners = async (page = currentPage) => {
    setLoading(true);
    setError("");

    // Validate ranges before sending
    ["bought", "sold"].forEach((field) => {
      const [from, to] = formData[field];
      if (from !== null && to !== null && to < from) {
        toast.warning(`"${field}" To value is less than From. Resetting To to null.`);
        formData[field][1] = null;
      }
    });

    try {
      const response = await axiosInstance.get(`api/propwire_properties/transfer-details`, {
        params: {
          zip: formData.zip,
          city: formData.city,
          state: formData.state,
          county: formData.county,
          period: formData.period,
          stillOwner: formData.stillOwner,
          propertyType: formData.propertyType,
          limit: leadsPerPage,
          skipEmpty: true,
          page,
          bought: Array.isArray(formData.bought) && formData.bought.some((v) => v !== null) ? formData.bought.map((v) => (v === "" || v === undefined ? null : Number(v))) : [null, null],
          sold: Array.isArray(formData.sold) && formData.sold.some((v) => v !== null) ? formData.sold.map((v) => (v === "" || v === undefined ? null : Number(v))) : [null, null],
        },
      });

      if (response.status === 200 && response.data.success) {
        const { data = [], total_found, page: currentPageFromAPI } = response.data;
        setOwners(data);
        setTotalOwners(total_found);
        setCurrentPage(currentPageFromAPI);
      } else {
        setOwners([]);
        toast.error(response.data?.message || "No data found.");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to fetch data.");
      toast.error("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  const changePage = async (page) => {
    if (page < 1 || (totalOwners && page > Math.ceil(totalOwners / leadsPerPage))) return;

    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("page", page);
    setSearchParams(newParams);
    await fetchOwners(page);
  };

  const totalPages = Math.ceil(totalOwners / leadsPerPage);

  const periodLabels = {
    last_month: "Last Month",
    last_3_months: "Last 3 Months",
    last_6_months: "Last 6 Months",
    last_year: "Last Year",
    last_2_years: "Last 2 Years",
    last_3_years: "Last 3 Years",
    last_4_years: "Last 4 Years",
    last_5_years: "Last 5 Years",
  };

  return (
    <div className="">
      {/* --- Search Form --- */}
      <form className="input_fields_container SP_search_container !grid md:!grid-cols-3 !gap-4" onSubmit={handleSubmit}>
        {["zip", "city", "state", "county"].map((field) => (
          <input key={field} type="text" name={field} className="input_field SP_input_field focus:!outline-none" placeholder={field.charAt(0).toUpperCase() + field.slice(1) + "..."} value={formData[field]} onChange={(e) => handleChange(field, e.target.value)} />
        ))}

        <div className="input_field SP_input_field focus:!outline-none bg-white w-full !pr-2">
          <select name="period" className="cursor-pointer w-full focus:!outline-none" value={formData.period || ""} onChange={(e) => handleChange("period", e.target.value)}>
            <option value="">-- Select Time Period --</option>
            {Object.entries(periodLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="input_field SP_input_field focus:!outline-none bg-white !flex !items-center justify-between !py-[12px]">
          <span className="text-sm font-medium text-gray-700">Still Owner:</span>
          <button type="button" onClick={() => handleChange("stillOwner", formData.stillOwner ? false : true)} className={`relative inline-flex h-6 w-12 items-center rounded-full transition-all duration-300 cursor-pointer ${formData.stillOwner ? "bg-green-500" : "bg-gray-300"}`}>
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-all duration-300 ${formData.stillOwner ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        <RangeInputs label="bought" value={formData.bought} onChange={(val) => handleChange("bought", val)} />
        <RangeInputs label="sold" value={formData.sold} onChange={(val) => handleChange("sold", val)} />

        <div className="input_field SP_input_field focus:!outline-none bg-white w-full !pr-2">
          <select name="propertyType" className="cursor-pointer w-full focus:!outline-none" value={formData.propertyType || ""} onChange={(e) => handleChange("propertyType", e.target.value)}>
            <option value="">-- Select Property Type --</option>
            {Object.entries(propertyTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-3 flex justify-center item-center">
          <button className="w-full md:max-w-xs text-white cursor-pointer text-xl !px-6 !py-2 rounded-sm !bg-[#008080] hover:!bg-[#007070]" type="submit" disabled={loading}>
            {loading ? "Searching..." : "Search"}
          </button>
        </div>

        {error && <div style={{ color: "red", gridColumn: "1 / -1" }}>{error}</div>}
      </form>

      {/* --- Loader --- */}
      {loading && (
        <div className="!p-4 !text-center !text-gray-500">
          <LoadingSpinner size={30} textSize={20} />
        </div>
      )}

      <div className="!grid !grid-col-1 !gap-6 !m-8">{!loading && owners.map((owner, idx) => <OwnerCard key={idx} owner={owner} index={idx} isOpen={openCard === idx} onToggle={() => setOpenCard(openCard === idx ? null : idx)} />)}</div>

      {/* --- Pagination --- */}
      {!loading && totalOwners > 0 && (
        <div className="flex flex-col items-center !mb-10 mt-4">
          <span className="text-sm text-gray-700">
            Showing <span className="font-semibold text-gray-900">{(currentPage - 1) * leadsPerPage + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(currentPage * leadsPerPage, totalOwners)}</span> of <span className="font-semibold text-gray-900">{totalOwners}</span> Entries
          </span>

          <PaginationButtons totalPages={totalPages} currentPage={currentPage} onChangePage={changePage} />
        </div>
      )}

      {!loading && owners.length === 0 && <p className="!text-center !mt-6 !text-gray-500">No data found.</p>}
    </div>
  );
};

export default TransferHistoryOwnerView;
